import classNames from 'classnames'
import { PointerEvent } from 'react'

import { BadgeArtState } from '../../../common/badge-art/index.js'
import { PlaytimeBadgeArt } from '../../../common/types/playtime.js'
import { badgeArtHtml } from './badge-art-html.js'
import { badgeArtMountRef } from './badge-art-mount.js'
import './badge-art.scss'
import { getBadgeGl } from './badge-gl-registry.js'
import { restElement, tiltElement } from './badge-tilt.js'

export interface BadgeArtProps {
    art: PlaytimeBadgeArt
    state: BadgeArtState
    /** Unique per instance on the page: every SVG id is prefixed with it. */
    idPrefix: string
    /** Width in px; omit to fill the container. */
    size?: number
    /**
     * Pointer tilt + moving highlight. `'self'` listens on the badge itself; `'scope'` only
     * applies the styling and expects an ancestor to call `tiltBadgeArtWithin` /
     * `restBadgeArtWithin` (the whole card tilts the badge, as in the prototype).
     * Off under prefers-reduced-motion via CSS.
     */
    tilt?: 'self' | 'scope'
    className?: string
    /**
     * Keep the 3D canvas mounted in the slot state too (the modal): the preview switch then
     * only swaps CSS classes and the pin cross-fades over the grey slot, instead of
     * re-mounting, re-drawing and popping in (Timo, 22.10.2026).
     */
    eager?: boolean
}

const handleTilt = (e: PointerEvent<HTMLSpanElement>) =>
    tiltElement(e.currentTarget, e.clientX, e.clientY)
const handleRest = (e: PointerEvent<HTMLSpanElement>) => restElement(e.currentTarget)

/** For a wider pointer scope (e.g. the badge card): tilt the badge art inside `scope`. */
export const tiltBadgeArtWithin = (scope: HTMLElement, clientX: number, clientY: number) => {
    const art = scope.querySelector<HTMLElement>('.badge-art--tilt')
    if (art) tiltElement(art, clientX, clientY)
}

export const restBadgeArtWithin = (scope: HTMLElement) => {
    const art = scope.querySelector<HTMLElement>('.badge-art--tilt')
    if (art) restElement(art)
}

/** Unlock flourish on the 3D badge inside `scope`; resolves at once without WebGL. */
export const spinBadgeArtWithin = (scope: HTMLElement): Promise<void> => {
    const art = scope.querySelector<HTMLElement>('.badge-art')
    const gl = art && getBadgeGl(art)
    return gl ? gl.spin() : Promise.resolve()
}

export const BadgeArt = ({ art, state, idPrefix, size, tilt, className, eager }: BadgeArtProps) => (
    <span
        // Re-mount when the art changes so the canvas is baked for the new spec (and, unless
        // eager, when the state changes: a slot has no canvas).
        key={`${idPrefix}-${eager ? 'eager' : state}-${art.shape}-${art.palette}-${art.tier}-${art.anniversaryYears ?? ''}`}
        // Active badges get the WebGL canvas (React 19 callback ref returns the cleanup).
        ref={badgeArtMountRef(art, eager ? 'active' : state)}
        className={classNames(
            'badge-art',
            tilt && 'badge-art--tilt',
            eager && state === 'slot' && 'badge-art--slot',
            className,
        )}
        style={size ? { width: size } : undefined}
        onPointerMove={tilt === 'self' ? handleTilt : undefined}
        onPointerLeave={tilt === 'self' ? handleRest : undefined}
    >
        {/* The SVG lives in its own span so React's innerHTML never touches the GL canvas
            that badge-art-mount.ts appends next to it. Our own generated markup. */}
        <span
            className="badge-art__svg"
            dangerouslySetInnerHTML={badgeArtHtml(art, state, idPrefix)}
        />
    </span>
)
